import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
    RiderInfo,
    CompleteUserData,
    UserIdData,
    SleepApneaLevel,
    InvestmentGoal,
    MedCount,
    BankruptcyChapter,
    EmploymentStatus,
    MaritalStatus,
} from '@amplify/athena-models';
import {parseHeightStringToInches} from '../helpers/parsers';
import UserData from './UserData';
import {anyCond, diagCond} from './UserData/types';
import merge from 'lodash.merge';
import Feature from '../components/FeatureFlag/Flags';
dayjs.extend(customParseFormat);

/**
 * Contains functions with no side effects used by CalculatorStore component
 */

export const isUserDataReadyForCalc = (userData: UserData): boolean => {
    const dob = userData?.phase1?.dateOfBirth;
    return !!dob && dayjs(dob, 'MM/DD/YYYY').isValid();
};

export const mapStateToEligibleProductsRequestData = (
    userData: UserData,
): {userData: CompleteUserData; userIdData: UserIdData} => {
    const {phase1, phase2, utm, partners, referral} = userData;
    const dateOfBirth = dayjs(phase1.dateOfBirth, 'MM/DD/YYYY');
    const dateOfBirthIso = dateOfBirth?.isValid() ? dateOfBirth.toISOString() : undefined;
    const heightInches = phase2.height ? parseHeightStringToInches(phase2.height) : 173;

    const hasCondition = (condition: string) => !!userData.phase2.diagCond.find((c) => c.label === condition)?.value;
    const hasBehavior = (behavior: string) => !!userData.phase2.behaviors.find((c) => c.label === behavior)?.value;

    const diabetes = hasCondition(diagCond.Diabetes);
    let diabetesType: 'type1' | 'type2' | null = null;
    if (diabetes) {
        diabetesType = phase2.diagDiabetesType ? 'type1' : 'type2';
    }

    let completeUserData: CompleteUserData = {
        stateId: phase1.zipCodeId,
        zip: phase1.zipCode,
        dateOfBirth: dateOfBirthIso,
        usedTobacco: phase2.usedTobacco,
        tobaccoLastUseDate: getStandardizedDateString(phase2.usedTobaccoLastDate),
        gender: phase1.gender as 'male' | 'female',
        heightInches,
        weightPounds: parseFloat(phase2.weight),
        diagAlcoholAbuse: hasCondition(diagCond.Alcohol),
        drugAlcoholTreatment: !!phase2.treatment,
        drugAlcoholTreatmentDate: getStandardizedDateString(phase2.treatmentWhen),
        diagDrugAbuse: hasCondition(diagCond.Drug),
        alcoDrugFreeYears: phase2.alcoDrugFreeYears ? Number(phase2.alcoDrugFreeYears) : null,
        diagMentalDisorder: hasCondition(diagCond.PsychDisorder),
        mentalDisorderHospitalization: !!phase2.diagPsychLastHosp,
        mentalDisorderDiagnoses: phase2.diagPsychCondition || null,
        mentalDisorderMedCount: (phase2.diagPsychMedication as MedCount) || null,
        behaviorDuiRecklessDriving: hasBehavior(anyCond.DUI),
        behaviorMovingViolation: hasBehavior(anyCond.MovingViolations),
        movingViolationsCount3Years: phase2.movingViolationsHowMany ? Number(phase2.movingViolationsHowMany) : null,
        duiDate: getStandardizedDateString(phase2.DUIWhen),
        behaviorSuspendedLicense: hasBehavior(anyCond.SuspendedLicense),
        suspendedLicenseDate: getStandardizedDateString(phase2.suspendedLicenseWhen),
        suspendedLicenseStill: !!phase2.suspendedLicenseStill,
        behaviorDrugAlcoholAbuse: hasBehavior(anyCond.AlcoholOrDrug),
        usedMarijuana: phase2.usedMarijuana,
        marijuanaLastUseDate: getStandardizedDateString(phase2.usedMarijuanaLastDate),
        marijuanaFrequencyOfUse: phase2.usedMarijuanaFrequency,
        criminalOffense: hasBehavior(anyCond.CriminalOffense),
        criminalOffenseDate: phase2.criminalOffenseWhen ? getStandardizedDateString(phase2.criminalOffenseWhen) : null,
        paroleCompletionYears: phase2.paroleComplete || null,
        asthma: hasCondition(diagCond.Asthma),
        asthmaHospitalization5Years: getBooleanOrNull(phase2.diagAsthmaLastHosp as any),
        diabetes,
        diabetesType,
        diabetesLastA1C: phase2.diagDiabetesA1C ? Number(phase2.diagDiabetesA1C) : null,
        diabetesTreatment: (phase2.diagDiabetesTreatments as any) || null,
        sleepApnea: hasCondition(diagCond.SleepApnea),
        sleepApneaCpap: getBooleanOrNull(phase2.diagSleepCPAP as any),
        sleepApneaLevel: (phase2.diagSleepLevel as SleepApneaLevel) || null,
        // TODO remove after verifying not in any rules
        sleeApneaLevel: (phase2.diagSleepLevel as SleepApneaLevel) || null,
        stroke: hasCondition(diagCond.Stroke),
        lastStrokeDate: getStandardizedDateString(phase2.diagStrokeWhen),
        highCholesterol: hasCondition(diagCond.HighCholesterol),
        latestCholesterolHdlRatio: phase2.diagHighCholHDL || null,
        highBloodPressure: hasCondition(diagCond.BloodPressure),
        lastLowBloodPressure: phase2.diagBloodPressLow,
        lastHighBloodPressure: phase2.diagBloodPressHigh,
        bloodPressureMedCount: (phase2.diagBloodPressMedication as MedCount) || null,
        heartAttack: hasCondition(diagCond.HeartAttack),
        lastHeartAttackDate: getStandardizedDateString(phase2.diagHeartAttackWhen),
        cancer: hasCondition(diagCond.Cancer),
        cancerBasalCell: getBooleanOrNull(phase2.diagCancerBasalCell as any),
        cancerLastTreatmentDate: getStandardizedDateString(phase2.diagCancerWhen),
        otherSeriousCondition: phase2.otherConditionDescribe,
        bankruptcy: hasBehavior(anyCond.Bankruptcy),
        bankruptcyChapter: (phase2.bankruptcyChapter as BankruptcyChapter) || null,
        bankruptcyYear: phase2.bankruptcyYear ? Number(phase2.bankruptcyYear) : null,
        bankruptcyIsOpen: !!phase2.bankruptcyIsOpen,
        hivAids: phase2.aids,
        currentlyEmployed: phase1.employmentStatus === 'employed',
        employmentStatus: getEmploymentStatusKey(phase1.employmentStatus, phase1.unemploymentReasonType),
        maritalStatus: getMaritalStatusKey(phase1.maritalStatus),
        disability: phase1.unemploymentReasonType == 'On Disability',
        preferredInvestmentRisk: phase1.investmentRisk || null,
        investmentGoals: <InvestmentGoal[]>phase1.features,
        intentGoal: (phase1.intentGoals as any) || [],
        preferredMonthlySpend: phase1.comfortableAmount,
        annualHouseholdIncomeRange: phase1.annualHouseholdIncome || null,
        annualHouseholdIncomeNumber: Number(phase1.annualHouseholdIncomeText),
        campaign: utm.campaign,
        utmSource: utm.source,
        utmMedium: utm.medium,
        utmOrigin: utm.origin,
        utmTerm: utm.term,
        utmContent: utm.content,
        utmAdgroup: utm.adgroup,
        wtStartTime: userData.wtStartTime,
        wtLeadFormTime: userData.wtLeadFormTime,
        tuneId: partners.tuneId,
        offerId: partners.offerId,
        offerName: partners.offerName,
        affiliateId: partners.affiliateId,
        partnerUserId: referral.partnerUserId,
        source: partners.source,
        consentAuthUrl: userData.consentAuthUrl,
        useProsperFlow: userData.useProsperFlow,
        usCitizenship: phase1.usCitizen ? 'US_CITIZEN' : null, // We only ask if true or false
        duiLast5years: getDuiLast5Years(phase2.DUIWhen),
        // The following are required by the type but not asked
        // explicilty in the walkthrough -- providing all negative responses
        weightLossSurgery: false,
        atrialFibrillation: false,
        defibrillator: false,
        organTransplant: false,
        cancerStage: null,
        felonyOffense: false,
        militaryCurrentDeployment: false,
        paroleProbation: false,
        duiMoreThan1Last2Years: null,
    };

    // TODO: Remove this code when ENG-1197 is done. Right now this adds dummy data so that the recommendation engine,
    //  quoting and approval can return valid results.
    if (phase1.amplifyVulPrequalify) {
        completeUserData = merge(
            {
                stateId: 4,
                zip: '85902',
                dateOfBirth: '1981-02-01T06:00:00.000Z',
                usedTobacco: false,
                tobaccoLastUseDate: null,
                gender: 'male',
                heightInches: 173,
                weightPounds: null,
                diagAlcoholAbuse: false,
                drugAlcoholTreatment: false,
                drugAlcoholTreatmentDate: null,
                diagDrugAbuse: false,
                alcoDrugFreeYears: null,
                diagMentalDisorder: false,
                mentalDisorderHospitalization: false,
                mentalDisorderDiagnoses: [],
                mentalDisorderMedCount: null,
                behaviorDuiRecklessDriving: false,
                behaviorMovingViolation: false,
                movingViolationsCount3Years: null,
                duiDate: null,
                behaviorSuspendedLicense: false,
                suspendedLicenseDate: null,
                suspendedLicenseStill: false,
                behaviorDrugAlcoholAbuse: false,
                usedMarijuana: false,
                marijuanaLastUseDate: null,
                marijuanaFrequencyOfUse: null,
                asthma: false,
                asthmaHospitalization5Years: null,
                diabetes: false,
                diabetesType: null,
                diabetesLastA1C: null,
                diabetesTreatment: null,
                sleepApnea: false,
                sleepApneaCpap: null,
                sleeApneaLevel: null,
                stroke: false,
                lastStrokeDate: null,
                highCholesterol: false,
                latestCholesterolHdlRatio: null,
                highBloodPressure: false,
                lastLowBloodPressure: '',
                lastHighBloodPressure: '',
                bloodPressureMedCount: null,
                heartAttack: false,
                lastHeartAttackDate: null,
                cancer: false,
                cancerBasalCell: null,
                cancerLastTreatmentDate: null,
                otherSeriousCondition: '',
                bankruptcy: false,
                bankruptcyChapter: null,
                bankruptcyYear: null,
                bankruptcyIsOpen: false,
                hivAids: false,
                currentlyEmployed: true,
                disability: false,
                preferredInvestmentRisk: 'high',
                investmentGoals: ['savings'],
                preferredMonthlySpend: 500,
                annualHouseholdIncomeRange: 5,
                annualHouseholdIncomeNumber: 12683957,
                campaign: '',
                utmSource: '',
                utmMedium: '',
                utmOrigin: '',
                utmTerm: '',
                utmContent: '',
                weightLossSurgery: false,
                atrialFibrillation: false,
                defibrillator: false,
                organTransplant: false,
                cancerStage: null,
                felonyOffense: false,
                militaryCurrentDeployment: false,
                paroleProbation: false,
                usCitizenship: null,
                duiMoreThan1Last2Years: null,
                duiLast5years: null,
            },
            completeUserData,
        );
    }

    if (userData.experiment[Feature.intentBuilder4456]) {
        completeUserData.intentBuilderResult = phase1.intentBuilderResult;
    }

    const userIdData: UserIdData = {
        id: userData.uuid,
        phoneNumber: '+1 ' + userData.phone,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
    };

    return {userData: completeUserData, userIdData};
};

export const mapAvailableRidersToLegacyState = (riders: RiderInfo | null): string[] => {
    return [riders?.chronic && 'Chronic', riders?.critical && 'Critical'];
};

function getStandardizedDateString(dateString: string): string {
    if (!dateString) {
        return null;
    }
    return dayjs(dateString, 'MM/YYYY').format('YYYY-MM-DD');
}

function getBooleanOrNull(b: '' | true | false) {
    if (b === '' || b == null) {
        return null;
    }
    return b;
}

function getEmploymentStatusKey(employmentStatus: string, unemploymentReasonType: string): EmploymentStatus {
    function getUnemployedReasonTypeKey(unemploymentReasonType: string): EmploymentStatus {
        switch (unemploymentReasonType?.toLowerCase()) {
            case 'unemployed':
                return 'UNEMPLOYED';
            case 'retired':
                return 'RETIRED';
            case 'student':
                return 'STUDENT';
            case 'homemaker':
                return 'HOMEMAKER_SPOUSE_EMPLOYED';
            case 'other':
                return 'OTHER';
            case 'on disability':
                return 'DISABILITY';
            default:
                return null;
        }
    }

    switch (employmentStatus?.toLowerCase()) {
        case 'employed':
            return 'EMPLOYED';
        case 'unemployed':
            return getUnemployedReasonTypeKey(unemploymentReasonType);
        default:
            return null;
    }
}

function getMaritalStatusKey(maritalStatus: string): MaritalStatus {
    switch (maritalStatus?.toLowerCase()) {
        case 'single':
            return 'SINGLE';
        case 'married':
            return 'MARRIED';
        case 'divorced':
            return 'DIVORCED';
        case 'separated':
            return 'SEPARATED';
        case 'widowed':
            return 'WIDOWED';
        default:
            return null;
    }
}

function getDuiLast5Years(duiWhen: string): boolean {
    if (!duiWhen) {
        return false;
    }
    const duiDate = dayjs(duiWhen, 'MM/YYYY').startOf('month');
    if (!duiDate.isValid()) {
        return false;
    }
    const fiveYearsAgo = dayjs().subtract(5, 'years').startOf('month');
    return !duiDate.isBefore(fiveYearsAgo);
}
